import React, { useEffect, useState } from "react";
import { process } from "@progress/kendo-data-query";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Label, Form, Button } from "reactstrap";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import moment from "moment";
import FeatherIcon from "feather-icons-react";
import { ColumnProps } from "../components/kendoComponents/columnProps/ColumnProps";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import LoaderSpin from "../components/loaderSpin/LoaderSpin";

import { SetCurrentActiveTab } from "../store/customizer/CustomizerSlice";
import ModalManualValidation from "../components/manualValidation/ModalManualValidation";
import "../assets/scss/kendo-theme-custom.scss";

import {
  fetchProcessRunLogs,
  fetchProcessVersionRunInfo,
} from "../store/app/ProcessRuns/ProcessRunsSlice";

const ApplicationLog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { versionId } = useParams();

  const searchParams = new URLSearchParams(location.search);

  const logs = useSelector((state) => state.processRunsReducer.processRunLogs);
  const processVersionRunInfo = useSelector(
    (state) => state.processRunsReducer.processVersionRunInfo || {}
  );
  const isSpinnerVisible = useSelector(
    (state) => state.dataQualityReducer.loading
  );
  const refreshCount = useSelector(
    (state) => state.dataQualityReducer.refreshCount
  );

  const [dataState, setDataState] = useState({
    sort: [
      {
        field: "code",
        dir: "asc",
      },
    ],
    skip: 0,
    take: 20,
  });

  const [dataResult, setDataResult] = useState(process(logs, dataState));

  useEffect(() => {
    dispatch(fetchProcessVersionRunInfo(versionId));
    dispatch(fetchProcessRunLogs(versionId));
  }, [navigate, refreshCount]);

  useEffect(() => {
    setDataState((prev) => ({
      ...{
        sort: [
          {
            field: "code",
            dir: "asc",
          },
        ],
      },
      skip: 0,
      take: 20,
    }));
    setDataResult(process(logs, dataState));
  }, [logs]);

  const currentActiveTab = useSelector(
    (state) => state.customizer.currentActiveTab
  );

  // Open modal window
  const toggleManualValidation = () => {
    setModal(!modal);
  };

  const onClickPrefect = (event) => {
    if (processVersionRunInfo.run_handle) {
      window.open(
        process.env.REACT_APP_PREFECT_URL + processVersionRunInfo.run_handle,
        "_blank"
      );
    }
  };

  // Toggle active state for Tab
  const toggle = (tab) => {
    if (currentActiveTab !== tab) dispatch(SetCurrentActiveTab(tab));
  };

  const [modal, setModal] = useState(false);

  return (
    <>
      <Form>
        {/* Row 1: Output Version Info */}
        <Row className="bg-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-center g-3">
              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column">
                <h5 className="form-row-title">Output Version Info</h5>
              </Col>
              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="outputVersionID" className="mb-1 small">Version</Label>
                <button
                  id="outputVersionID"
                  className="form-control-plaintext mb-0 px-2 py-1 border border-primary rounded bg-medium-primary text-truncate w-100 d-inline-block text-start"
                  onClick={() => {
                    navigate(
                      "/versioni/detail/" +
                      processVersionRunInfo.version_id +
                      "?" +
                      searchParams
                    );
                  }}
                  title={processVersionRunInfo.version_id}
                >
                  {processVersionRunInfo.version_id}
                </button>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="outputVersionProductTypeName" className="mb-1 small">Data Product</Label>
                <button
                  id="outputVersionProductTypeName"
                  type="button"
                  className="form-control-plaintext mb-0 px-2 py-1 border border-primary rounded bg-medium-primary text-truncate w-100 d-inline-block text-start"
                  onClick={() => {
                    navigate(
                      "/data-catalog/details/" +
                      processVersionRunInfo.product_type_id +
                      "?" +
                      searchParams
                    );
                  }}
                  title={processVersionRunInfo.product_name}
                >
                  {processVersionRunInfo.product_name}
                </button>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="outputVersionReferenceDate" className="mb-1 small">Reference Date</Label>
                <span
                  id="outputVersionReferenceDate"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={moment(processVersionRunInfo.reference_date).format("DD/MM/YYYY")}
                >
                  {moment(processVersionRunInfo.reference_date).format("DD/MM/YYYY")}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="outputVersionExtendedStatus" className="mb-1 small">Version Status</Label>
                <span
                  id="outputVersionExtendedStatus"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={`${processVersionRunInfo.status_name} - ${processVersionRunInfo.ext_status_name}`}
                >
                  {processVersionRunInfo.status_name} - {processVersionRunInfo.ext_status_name}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="ms-md-auto d-flex align-items-end">
                <Button
                  color="primary"
                  outline
                  size="md"
                  onClick={onClickPrefect}
                >
                  <FeatherIcon icon="external-link" size={14} className="me-1" />
                  Log Prefect
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Row 2: Run Info */}
        <Row className="bg-very-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-center g-3">
              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column">
                <h5 className="form-row-title">Run info</h5>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="runID" className="mb-1 small">Run ID</Label>
                <span
                  id="runID"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.run_id}
                >
                  {processVersionRunInfo.run_id}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="startTimeStamp" className="mb-1 small">Start</Label>
                <span
                  id="startTimeStamp"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={moment(processVersionRunInfo.run_start_ts).format("DD/MM/YYYY")}
                >
                  {moment(processVersionRunInfo.run_start_ts).format("DD/MM/YYYY")}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="duration" className="mb-1 small">Duration</Label>
                <span
                  id="duration"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={`${processVersionRunInfo.run_status == "Timeouted" ? "Timeouted after " : ""}${processVersionRunInfo.duration}`}
                >
                  {processVersionRunInfo.run_status == "Timeouted"
                    ? "Timeouted after "
                    : ""}
                  {processVersionRunInfo.duration}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="runStatus" className="mb-1 small">Run Status</Label>
                <span
                  id="runStatus"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.run_status}
                >
                  {processVersionRunInfo.run_status}
                </span>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Row 3: Process Info */}
        <Row className="bg-light-primary px-4 py-2">
          <Col>
            <Row className="align-items-center g-3">
              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column">
                <h5 className="form-row-title">Process Info</h5>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="processName" className="mb-1 small">Process</Label>
                <span
                  id="processName"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.process_name}
                >
                  {processVersionRunInfo.process_name}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="processType" className="mb-1 small">Process Type</Label>
                <span
                  id="processType"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.process_type}
                >
                  {processVersionRunInfo.process_type}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="revisionNumber" className="mb-1 small">Revision number</Label>
                <span
                  id="revisionNumber"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.revision_num}
                >
                  {processVersionRunInfo.revision_num}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="releaseData" className="mb-1 small">Revision Date</Label>
                <span
                  id="releaseData"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={moment(processVersionRunInfo.relase_date).format("DD/MM/YYYY")}
                >
                  {moment(processVersionRunInfo.relase_date).format("DD/MM/YYYY")}
                </span>
              </Col>

              <Col xs="12" md="auto" lg="auto" className="d-flex flex-column min-w-0">
                <Label for="revisionReleaseNotes" className="mb-1 small">Revision notes</Label>
                <span
                  id="revisionReleaseNotes"
                  className="form-control-plaintext mb-0 px-2 py-1 text-truncate w-100 d-inline-block"
                  title={processVersionRunInfo.release_notes || "N/A"}
                >
                  {processVersionRunInfo.release_notes || "N/A"}
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <Row className="px-4 my-3"></Row>

      <Container fluid className="px-4">
        <Row>
          <Col>
            <div className="grid_task_container_short border">
              <Grid
                style={{ height: "100%" }}
                pageable={true}
                sortable={true}
                total={dataResult?.length}
                data={dataResult}
                {...dataState}
                groupable={true}
                onDataStateChange={(e) => {
                  setDataState(e.dataState);
                  setDataResult(process(logs, e.dataState));
                }}
              >
                <GridColumn
                  {...ColumnProps("log_ts", dataState)}
                  title="Timestamp"
                />
                <GridColumn {...ColumnProps("flow", dataState)} title="Flow" />
                <GridColumn
                  {...ColumnProps("task", dataState)}
                  title="Flow Subname"
                />
                <GridColumn
                  {...ColumnProps("message", dataState)}
                  title="Message"
                />
              </Grid>
            </div>
          </Col>
        </Row>
        <LoaderSpin isVisible={isSpinnerVisible} />
      </Container>
      <ModalManualValidation
        modal={modal}
        toggleManualValidation={toggleManualValidation}
      />
    </>
  );
};

export default ApplicationLog;
